import Head from "next/head";
import * as format from '@format';
import Plus from '@components/Icons/Plus';
import Check from '@components/Icons/Check';
import DownloadSimple from '@components/Icons/DownloadSimple';
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import MainPanelSubLayout from "@/layouts/MainPanelSubLayout";
import useNoodleApi from "@/hooks/useNoodleApi";
import { getUserWorkflowById } from "@/tsClient/workflows";
import userWorkflowToContext from "@/helpers/userWorkflowToContext";
import Buttons from "@/components/Buttons";
import UploadBankStatementsModal, { ModalConfig } from "@/modals/UploadBankStatementModal";
import { getBankStatementOrganizerRequest, updateBankStatementOrganizerRequest } from "@/tsClient/bankAccountIntegration";
import { getDocumentById } from "@/tsClient/documents";
import NoodleAI from "@/components/Icons/NoodleAI";
import CollapsibleArea from "@/components/CollapsibleArea";

import DocumentPreview from '@/lib/DocumentPreview/DocumentPreview';
import Tabs from "@/components/DesignLibrary/Tabs";
import Button from "@/components/DesignLibrary/Button";
import { Select } from "@/components/DesignLibrary/Atoms";
import PrettyJson from "@/components/Document/DocumentPreview/PrettyJson";
import ProgressIndicator from "@/components/ProgressIndicator";
import classNames from "classnames";
import { BankAccountIntegrationRequestStatus } from "@/typings/api-models";
import Reopen from '@components/Icons/Reopen';
import { useToast } from "@/hooks";
import { ToastTypeVariants } from "@/context/ToastContext";
import s from './BankStatementOrganizerDetail.module.scss';

type Props = {
    bankStatementOrganizerId: string;
};

const BankStatementsOrganizerDetail: React.FC<Props> = ({ bankStatementOrganizerId }) => {
  const addToast = useToast();
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [uploadBankStatementsModalOpen, setUploadBankStatementsModalOpen] = useState(false);
  const [institutions, setInstitutions] = useState<string[]>([]);
  const [accountNames, setAccountNames] = useState<string[]>([]);
  const [selectedInstitution, setSelectedInstitution] = useState<string | null>(null);
  const [selectedAccountName, setSelectedAccountName] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [modalConfig, setModalConfig] = useState<ModalConfig | undefined>(undefined);
  const [fetchedDocuments, setFetchedDocuments] = useState<Record<string, { fileName: string }>>({});

  const {
    data: workflowData,
    getData: getWorkflow,
  } = useNoodleApi(getUserWorkflowById, { toastOnError: true });

  const {
    getData: getDocumentByIdFn,
    fetchingState: { isFetching: isFetchingDocument },
  } = useNoodleApi(getDocumentById);

  const {
    data: bankStatementOrganizerRequest,
    getData: getBankStatementOrganizerRequestFn,
  } = useNoodleApi(getBankStatementOrganizerRequest, { toastOnError: true });

  const {
    fetchingState: { isFetching: updatingRequestStatus },
    getData: updateBankStatementOrganizerRequestFn,
  } = useNoodleApi(updateBankStatementOrganizerRequest, { toastOnError: true });

  const onRenameDocument = useCallback(async (): Promise<void> => {
    if (bankStatementOrganizerRequest?.documents) {
      await Promise.all(bankStatementOrganizerRequest.documents.map(async document => {
        const { data } = await getDocumentByIdFn({ documentId: document.documentId });
        setFetchedDocuments(prev => ({
          ...prev,
          [document.documentId]: { fileName: data?.fileName ?? document.documentName },
        }));
      }));
    }
  }, [bankStatementOrganizerRequest?.documents, getDocumentByIdFn]);

  const handleBankAccountAdded = (): void => {
    getBankStatementOrganizerRequestFn({
      bankAccountIntegrationId: bankStatementOrganizerId,
    });
  };
  const isBankIntegrationRequestComplete = useMemo(() => bankStatementOrganizerRequest?.status === BankAccountIntegrationRequestStatus.COMPLETED,
    [bankStatementOrganizerRequest?.status]);

  const handleUpdateRequestStatus = useCallback(async () => {
    if (bankStatementOrganizerRequest) {
      await updateBankStatementOrganizerRequestFn({
        bankAccountIntegrationId: bankStatementOrganizerRequest.id,
        updates: {
          status: isBankIntegrationRequestComplete ? BankAccountIntegrationRequestStatus.IN_PROGRESS : BankAccountIntegrationRequestStatus.COMPLETED,
        },
      });
      getBankStatementOrganizerRequestFn({ bankAccountIntegrationId: bankStatementOrganizerId });
      addToast(ToastTypeVariants.SUCCESS, 'Bank integration request completed successfully.');
    }
  }, [bankStatementOrganizerRequest?.id, updateBankStatementOrganizerRequestFn]);

  useEffect(() => {
    if(bankStatementOrganizerRequest && !bankStatementOrganizerRequest.documents?.length) {
      setUploadBankStatementsModalOpen(true);
    }else{
      setUploadBankStatementsModalOpen(false);
    }
  },[bankStatementOrganizerRequest]);

  useEffect(() => {
    getBankStatementOrganizerRequestFn({ bankAccountIntegrationId: bankStatementOrganizerId });
  }, [bankStatementOrganizerId, getBankStatementOrganizerRequestFn]);

  useEffect(() => {
    onRenameDocument();
  }, [onRenameDocument]);

  useEffect(() => {
    if (bankStatementOrganizerRequest?.documents) {
      const institutionSet = new Set<string>();

      bankStatementOrganizerRequest.documents.forEach(document => {
        if (document.institutionName) {
          institutionSet.add(document.institutionName);
        }
      });

      const institutionsArr = Array.from(institutionSet);
      setInstitutions(institutionsArr);

      if (institutionsArr.length) {
        setSelectedInstitution(institutionsArr[0]);
      }
    }
  }, [bankStatementOrganizerRequest?.documents]);

  useEffect(() => {
    if(selectedInstitution && bankStatementOrganizerRequest?.documents) {
      const accountNameSet = new Set<string>();
      bankStatementOrganizerRequest.documents.forEach(document => {
        if (document.accountName && document.institutionName === selectedInstitution) {
          accountNameSet.add(document.accountName);
        }
        const accountNamesArr = Array.from(accountNameSet);
        setAccountNames(accountNamesArr);
        if (accountNamesArr.length) {
          setSelectedAccountName(accountNamesArr[0]);
        }
      });
    }
  },[selectedInstitution, bankStatementOrganizerRequest?.documents]);

  const documentsSelectOptions = useMemo(() => bankStatementOrganizerRequest?.documents
    ?.filter(document => document.institutionName === selectedInstitution && document.accountName === selectedAccountName)
    .map(document => ({
      key: document.documentId,
      label: fetchedDocuments[document.documentId]?.fileName || document.documentName,
    })) || [],
  [bankStatementOrganizerRequest?.documents, selectedInstitution, selectedAccountName, fetchedDocuments]);

  useEffect(() => {
    if (selectedInstitution && selectedAccountName && bankStatementOrganizerRequest?.documents?.length) {
      if (documentsSelectOptions.length) {
        setSelectedDocumentId(documentsSelectOptions[0].key);
      }
    }
  }, [selectedInstitution, selectedAccountName, bankStatementOrganizerRequest?.documents, documentsSelectOptions]);

  useEffect(() => {
    if (bankStatementOrganizerRequest?.userWorkflowId) {
      getWorkflow({ userWorkflowId: bankStatementOrganizerRequest?.userWorkflowId });
    }
  }, [bankStatementOrganizerRequest?.userWorkflowId, getWorkflow]);

  useEffect(() => {
    if (!bankStatementOrganizerRequest?.documents?.length) {
      return;
    }
    const lastUpdatedDate = bankStatementOrganizerRequest?.documents
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]?.createdAt;
    setLastUpdated(new Date(lastUpdatedDate));
  },[bankStatementOrganizerRequest?.documents]);

  const jsonData = JSON.stringify(bankStatementOrganizerRequest?.documents?.find(doc => (doc.accountName === selectedAccountName)
    && doc.institutionName === selectedInstitution
    && doc.documentId === selectedDocumentId)?.extractedData);

  return <>
    <Head>
      <title>Bank Statement Organizer</title>
    </Head>
    <MainPanelSubLayout
      homeTabTitle="Bank Statements"
      header={{
        actions: [
          {
            disabled: !bankStatementOrganizerRequest?.documents?.length,
            icon: isBankIntegrationRequestComplete ? Reopen : Check,
            isFetching: updatingRequestStatus,
            label: isBankIntegrationRequestComplete ? 'Re-open Request' : 'Mark as Completed',
            onClick: () => handleUpdateRequestStatus(),
          },
          {
            disabled: isBankIntegrationRequestComplete,
            icon: Plus,
            isSecondary: true,
            label: `Add Bank`,
            onClick: () => setUploadBankStatementsModalOpen(true),
          },
          {
            icon: DownloadSimple,
            isSecondary: true,
            label: `Download all Statements`,
            onClick: () => null,
          },
        ],
        description: (
          <>
            {lastUpdated && (
              <p className="body-sm" style={{ color: 'var(--color-gray-75)' }}>
                Last activity: {format.datetimeLocalTZ.withoutTimezone({ datetime: lastUpdated.toISOString() })}
              </p>
            )}
          </>
        ),
        title: "Bank Statements",
      }}
      pageContext={{
        isFetching: !workflowData,
        items: workflowData ? userWorkflowToContext(workflowData) : [],
      }}
      rightPanel={{
        component: (
          <Fragment key={selectedDocumentId}>
            <div className={s.aiAnalysis}>
              <h2 className="body-lg-bold">
                <NoodleAI size={24} /> <span className="ai-label">Run AI Analysis</span>
              </h2>
              <p className="body-sm">Analyzes recent transactions using your rules to generate a detailed summary report.</p>
              <Button variant="ai" size="md" fullWidth disabled>Run Analysis Agent (Coming soon)</Button>
            </div>
            <CollapsibleArea title="View Extracted Data" openTitle="Hide Extracted Data">
              {!bankStatementOrganizerRequest?.documents?.length
                ? <span className={classNames(s.aiNotExtracted, 'caption')}>No Data</span>
                : <>
                  {jsonData
                    ? <PrettyJson
                      noBorder
                      stringifiedJson={jsonData}
                    />
                    : <div className={s.aiNotExtracted}>
                      <ProgressIndicator />
                    </div>}
                </>}
            </CollapsibleArea>
          </Fragment>
        ),
        id: 'extracted-data',
        title: 'Extracted Data',
      }}
    >
      <div className={s.fileDetailContainer}>
        <Tabs
          checked={selectedInstitution ?? undefined}
          inputName="institutions"
          inputs={(institutions ?? []).map(institution => ({
            id: institution,
            label: institution,
          }))}
          onChange={val => setSelectedInstitution(val)}
        />

        <div className={s.accountTypeSelector}>
          <span className="caption-bold">Accounts Types:</span>
          <>
            {
              (accountNames ?? [])?.map(accountName => (
                <Button
                  key={accountName}
                  onClick={() => setSelectedAccountName(accountName)}
                  size="xs"
                  variant="secondary"
                  disabled={selectedAccountName === accountName}
                >
                  {accountName}
                </Button>
              ))
            }
            <Button
              onClick={() => {
                setUploadBankStatementsModalOpen(true);
                setModalConfig({
                  disabledFields: ['INSTITUTION_NAME'],
                  fieldValues: {
                    institutionName: selectedInstitution ?? '',
                  },
                  step: 'manual',
                });
              }}
              disabled={isBankIntegrationRequestComplete}
              size="xs"
              variant="secondary"
            >
              <Plus style={{ marginRight: 4 }} />
              Add account
            </Button>
          </>
        </div>

        <div className={s.fileSelectorSection}>
          <div className={s.filesList}>
            <Select
              label="Select File"
              hideLabel
              disabled={isFetchingDocument}
              canClearSelection={false}
              options={(isFetchingDocument || documentsSelectOptions.length === 0) ? [{ key: 'loading', label: 'Loading...'}] : documentsSelectOptions}
              value={(isFetchingDocument || documentsSelectOptions.length === 0)
                ? 'loading'
                : (documentsSelectOptions.find(doc => doc.key === selectedDocumentId)?.key || documentsSelectOptions[0].key)}
              onChange={(doc) => setSelectedDocumentId(doc)}
            />
          </div>
          <Buttons
            className={s.addNewBankStatementBtn}
            isWrapper
            isFullWidth
            disabled={isBankIntegrationRequestComplete}
            onClick={() => {
              setUploadBankStatementsModalOpen(true);
              setModalConfig({
                disabledFields: ['INSTITUTION_NAME', 'ACCOUNT_NAME'],
                fieldValues: {
                  accountName: selectedAccountName ?? '',
                  institutionName: selectedInstitution ?? '',
                },
                step: 'manual',
              });
            }
            }>
            <Plus />
            Add statements
          </Buttons>
        </div>
        <div className={s.documentContainer}>
          {
            bankStatementOrganizerRequest?.documents?.length
              ? <>
                {bankStatementOrganizerRequest?.documents
                  ?.filter(doc => (doc.accountName===selectedAccountName)
                && doc.institutionName===selectedInstitution
                && doc.documentId === selectedDocumentId).map((document) => (
                    <DocumentPreview onRename={onRenameDocument} key={document.id} documentId={document.documentId} isModal />
                  ))}
              </>
              : null
          }
        </div>
      </div>
    </MainPanelSubLayout>
    {bankStatementOrganizerRequest && uploadBankStatementsModalOpen && (
      <UploadBankStatementsModal
        bankAccountIntegrationId={bankStatementOrganizerId}
        creatorId={bankStatementOrganizerRequest.creatorId}
        onClose={() => {
          setUploadBankStatementsModalOpen(false);
          setModalConfig(undefined);
        }}
        onBankAccountAdded={handleBankAccountAdded}
        config={modalConfig}
      />
    )}
  </>;
};

export default BankStatementsOrganizerDetail;
